/**
 * @fileoverview Connect won-deal onboarding events to recorded steps and explicitly selected retries. Backend event/effect identities own progress and recovery; the workspace owns filters/detail selection. `kc-internal-backend/functions/client_won.py` owns business execution. Retrying a step must use its recorded acknowledgement boundary rather than treating a failed dashboard status as proof that no effect occurred.
 */

import React, { useState, useEffect, useContext, useCallback } from 'react';
import { AppDataTable, AppColumn } from '../shared/AppDataTable';
import { AppButton, AppTag, AppModal, AppDropdown, AppTextInput, AppSpinner, AppCalendar } from '../shared';
import api from '../../api';
import ToastContext from '../../ToastContext';
import AppIcon from "../shared/AppIcon";

const STEPS = [
{ key: 'clockify', label: 'Clockify Project', tsField: 'clockify_completed_at' },
{ key: 'trello_label', label: 'Trello Label', tsField: 'trello_label_completed_at' },
{ key: 'slack_accounting', label: 'Notify Accounting', tsField: 'slack_accounting_at' },
{ key: 'slack_attorney', label: 'Notify Attorney', tsField: 'slack_attorney_dm_at' },
{ key: 'slack_kimberly', label: 'Notify Kimberly', tsField: 'slack_kimberly_at' },
{ key: 'vroom_email', label: 'Vroom Email', tsField: 'vroom_email_sent_at' },
{ key: 'db_matter', label: 'DB Matter', tsField: 'db_matter_completed_at' },
{ key: 'drive_folders', label: 'Drive Folders', tsField: 'drive_completed_at' },
{ key: 'old_drive_migration', label: 'Old Folder Migration', tsField: null },
{ key: 'pipedrive_files', label: 'Pipedrive Files', tsField: 'pipedrive_files_moved_at' },
{ key: 'import_hours', label: 'Import Hours', tsField: 'hours_imported_at' }];


const STATUS_OPTIONS = [
{ label: 'All', value: 'all' },
{ label: 'Received', value: 'received' },
{ label: 'Processing', value: 'processing' },
{ label: 'Completed', value: 'completed' },
{ label: 'Completed with Errors', value: 'completed_with_errors' },
{ label: 'Error', value: 'error' }];




function ClientWonEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [retryingStep, setRetryingStep] = useState(null);

  const showToast = useContext(ToastContext);

  const fetchEvents = useCallback(

  async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await api.get('/client-won/events', { params });
      setEvents(response.data.events || []);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error fetching events:', error);
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load client won events', life: 3000 });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm, startDate, endDate, showToast]);

  useEffect(() => {fetchEvents();}, [fetchEvents]);

  // Auto-refresh when items are in processing/received state
  useEffect(() => {
    const hasPending = events.some((e) => e.status === 'received' || e.status === 'processing');
    if (hasPending) {
      const interval = setInterval(fetchEvents, 30000);
      return () => clearInterval(interval);
    }
  }, [events, fetchEvents]);

  const getStatusSeverity =

  (status) => {
    switch (status) {
      case 'received':return 'info';
      case 'processing':return 'warning';
      case 'completed':return 'success';
      case 'completed_with_errors':return 'warning';
      case 'error':return 'danger';
      default:return null;
    }
  };

  const getStatusLabel =

  (status) => {
    const opt = STATUS_OPTIONS.find((o) => o.value === status);
    return opt ? opt.label : status;
  };

  const statusBodyTemplate =

  (row) =>
  <AppTag value={getStatusLabel(row.status)} severity={getStatusSeverity(row.status)} />;


  const dateBodyTemplate =

  (row, col) => {
    const val = row[col.field];
    if (!val) return '-';
    return new Date(val).toLocaleString();
  };

  const stepsBodyTemplate =

  (row) => {
    const completedCount = STEPS.filter((s) => row[s.tsField]).length;
    return `${completedCount}/${STEPS.length}`;
  };

  const viewDetails =

  async (event) => {
    try {
      const response = await api.get(`/client-won/events/${event.id}`);
      setSelectedEvent(response.data);
      setDetailVisible(true);
    } catch (error) {
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to load event details', life: 3000 });
    }
  };

  const retryEvent =

  async (eventId) => {
    try {
      const response = await api.post(`/client-won/events/${eventId}/retry`);
      if (response.data.success) {
        showToast({ severity: 'success', summary: 'Success', detail: 'Event re-queued for processing', life: 3000 });
        fetchEvents();
        setDetailVisible(false);
      }
    } catch (error) {
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to retry event', life: 3000 });
    }
  };

  const retryStep =

  async (eventId, stepName) => {
    setRetryingStep(stepName);
    try {
      const response = await api.post(`/client-won/events/${eventId}/retry-step`, { step: stepName });
      if (response.data.success) {
        showToast({ severity: 'success', summary: 'Success', detail: `Step "${stepName}" retried successfully`, life: 3000 });
        // Refresh detail
        const refreshed = await api.get(`/client-won/events/${eventId}`);
        setSelectedEvent(refreshed.data);
      } else {
        showToast({ severity: 'warning', summary: 'Warning', detail: response.data.error || 'Step retry had issues', life: 3000 });
        const refreshed = await api.get(`/client-won/events/${eventId}`);
        setSelectedEvent(refreshed.data);
      }
    } catch (error) {
      showToast({ severity: 'error', summary: 'Error', detail: 'Failed to retry step', life: 3000 });
    } finally {
      setRetryingStep(null);
    }
  };

  const getStepIcon =

  (stepResult, tsValue) => {
    if (!stepResult && !tsValue) return 'pi pi-minus-circle text-400';
    if (stepResult?.skipped) return 'pi pi-forward text-400';
    if (stepResult?.queued) return 'pi pi-clock text-blue-500';
    if (stepResult?.success === false) return 'pi pi-times-circle text-red-500';
    if (tsValue || stepResult?.success) return 'pi pi-check-circle text-green-500';
    return 'pi pi-minus-circle text-400';
  };

  const renderDetailDialog =

  () => {
    if (!selectedEvent) return null;
    const stepResults = selectedEvent.step_results || {};

    return (
      <AppModal
        title="Client Won Event Details"
        visible={detailVisible}
        width="900px"
        onClose={() => setDetailVisible(false)}>

                <div className="grid">
                    {/* Deal Info */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Deal Information</h3>
                            <div className="grid">
                                <div className="col-6"><strong>Deal Title:</strong> {selectedEvent.deal_title || '-'}</div>
                                <div className="col-6"><strong>Case ID:</strong> {selectedEvent.case_id || '-'}</div>
                                <div className="col-6"><strong>Defendant:</strong> {selectedEvent.defendant_name || '-'}</div>
                                <div className="col-6"><strong>Owner:</strong> {selectedEvent.owner_name || '-'}</div>
                                <div className="col-6"><strong>Won Time:</strong> {selectedEvent.won_time || '-'}</div>
                                <div className="col-6"><strong>Pipedrive Deal ID:</strong> {selectedEvent.pipedrive_deal_id}</div>
                            </div>
                        </div>
                    </div>

                    {/* Client Info */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Client Information</h3>
                            <div className="grid">
                                <div className="col-6"><strong>Name:</strong> {selectedEvent.client_name || '-'}</div>
                                <div className="col-6"><strong>Email:</strong> {selectedEvent.client_email || '-'}</div>
                                <div className="col-6"><strong>Phone:</strong> {selectedEvent.client_phone || '-'}</div>
                                <div className="col-6"><strong>Person ID:</strong> {selectedEvent.pipedrive_person_id || '-'}</div>
                            </div>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1 mb-3">
                            <div className="flex justify-content-between align-items-center mb-3">
                                <h3 className="mt-0 mb-0" style={{ color: 'var(--text-color)' }}>Status</h3>
                                {(selectedEvent.status === 'error' || selectedEvent.status === 'completed_with_errors') &&
                <AppButton
                  label="Retry All"
                  icon="pi pi-refresh"
                  variant="secondary"
                  onClick={() => retryEvent(selectedEvent.id)} />}


                            </div>
                            <div className="grid">
                                <div className="col-6">
                                    <strong>Status:</strong>{' '}
                                    <AppTag value={getStatusLabel(selectedEvent.status)} severity={getStatusSeverity(selectedEvent.status)} />
                                </div>
                                <div className="col-6">
                                    <strong>Default Attorney:</strong> {selectedEvent.used_default_attorney ? 'Yes' : 'No'}
                                </div>
                                {selectedEvent.error_message &&
                <div className="col-12">
                                        <strong>Error:</strong>
                                        <div className="text-sm text-red-500 mt-1">{selectedEvent.error_message}</div>
                                    </div>}

                            </div>
                        </div>
                    </div>

                    {/* Step Checklist */}
                    <div className="col-12">
                        <div className="surface-card p-3 border-round shadow-1">
                            <h3 className="mt-0 mb-3" style={{ color: 'var(--text-color)' }}>Steps</h3>
                            {STEPS.map(

                            (step) => {
                const result = stepResults[step.key];
                const tsValue = selectedEvent[step.tsField];
                const failed = result && result.success === false && !result.skipped;

                return (
                  <div key={step.key} className="flex align-items-center justify-content-between py-2 border-bottom-1 surface-border">
                                        <div className="flex align-items-center gap-2">
                                            <AppIcon icon={getStepIcon(result, tsValue)} />
                                            <span className="font-medium">{step.label}</span>
                                            {result?.skipped && <span className="text-sm text-400">(skipped)</span>}
                                            {result?.queued && <span className="text-sm text-blue-500">(queued - 6 min delay)</span>}
                                        </div>
                                        <div className="flex align-items-center gap-2">
                                            {tsValue &&
                      <span className="text-sm text-600">{new Date(tsValue).toLocaleString()}</span>}

                                            {result?.error &&
                      <span className="text-sm text-red-500" title={result.error}>
                                                    {result.error.length > 50 ? result.error.substring(0, 50) + '...' : result.error}
                                                </span>}

                                            {failed &&
                      <AppButton
                        icon="pi pi-refresh"
                        variant="secondary"
                        size="small"
                        loading={retryingStep === step.key}
                        onClick={() => retryStep(selectedEvent.id, step.key)} />}


                                        </div>
                                    </div>);

              })}
                        </div>
                    </div>
                </div>
            </AppModal>);

  };

  return (
    <div className="client-won-events">
            {/* Filters */}
            <div className="surface-card p-4 mb-4 border-round">
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label htmlFor="cw-status-filter" className="block mb-2">Status</label>
                        <AppDropdown
              id="cw-status-filter"
              value={statusFilter}
              options={STATUS_OPTIONS}
              onChange={(e) => setStatusFilter(e.value)} />

                    </div>
                    <div className="col-12 md:col-3">
                        <label htmlFor="cw-search" className="block mb-2">Search (Title/Client/Case ID)</label>
                        <AppTextInput
              id="cw-search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Type to search..." />

                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block mb-2">Start Date</label>
                        <AppCalendar value={startDate} onChange={(e) => setStartDate(e.value)} />
                    </div>
                    <div className="col-12 md:col-2">
                        <label className="block mb-2">End Date</label>
                        <AppCalendar value={endDate} onChange={(e) => setEndDate(e.value)} />
                    </div>
                    <div className="col-12 md:col-2 flex align-items-end">
                        <AppButton label="Refresh" icon="pi pi-refresh" onClick={fetchEvents} />
                    </div>
                </div>
                <div className="text-sm text-600 mt-2">
                    Last refreshed: {lastRefresh.toLocaleTimeString()}
                    {events.some((e) => e.status === 'received' || e.status === 'processing') &&
          ' \u2022 Auto-refreshing every 30 seconds'}

                </div>
            </div>

            {/* Data Table */}
            <div className="surface-card p-4 border-round">
                {loading && events.length === 0 ?
        <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <AppSpinner />
                    </div> :

        <AppDataTable
          value={events}
          paginator
          rows={25}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage="No client won events found"
          responsiveLayout="scroll"
          stripedRows
          sortField="created_at"
          sortOrder={-1}
          onRowClick={(e) => viewDetails(e.data)}
          rowClassName={() => 'cursor-pointer'}>

                        <AppColumn field="status" header="Status" body={statusBodyTemplate} sortable />
                        <AppColumn field="deal_title" header="Deal Title" sortable />
                        <AppColumn field="client_name" header="Client" sortable />
                        <AppColumn field="defendant_name" header="Defendant" sortable />
                        <AppColumn field="case_id" header="Case ID" sortable />
                        <AppColumn header="Steps" body={stepsBodyTemplate} style={{ width: '80px' }} />
                        <AppColumn field="created_at" header="Created" body={dateBodyTemplate} sortable />
                    </AppDataTable>}

            </div>

            {renderDetailDialog()}
        </div>);

}

export default ClientWonEvents;
